import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useCurrentProject } from '@/shared/hooks/useCurrentProject';
import { BaseLayout } from '@/shared/components/layout';
// Importing for the side effects: ``registerTour`` runs at module load.
import { OBSERVATIONS_DETAIL_TOUR_ID } from '@/features/tours/observations/detail';
import { OBSERVATIONS_COMBINED_TOUR_ID } from '@/features/tours/observations/combined';
import { useTour } from '@/features/tours';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/shared/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger,
} from '@/shared/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import {
  ArrowLeft,
  Paperclip,
  Trash2,
  Upload,
  Lock,
  Crosshair,
  Users,
  ShieldAlert,
  FileText,
  Calendar,
  User,
  CheckCircle,
  AlertCircle,
  Circle,
  Printer,
  MoreHorizontal,
} from 'lucide-react';
import { useObservation, useObservationMutations } from '../hooks';
import { STATUS_LABELS, PRIORITY_LABELS, type ObservationStatus } from '../types';
import { AttachmentPhotoGallery, isLikelyImageAttachment } from '@/shared/components/AttachmentPhotoGallery';
import { useAllUserProjects } from '@/shared/hooks/useUserProjects';

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const STATUS_BADGE: Record<ObservationStatus, string> = {
  OPEN: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  OVERDUE: 'bg-red-100 text-red-800',
  CLOSED: 'bg-gray-100 text-gray-700',
};

const PRIORITY_BADGE: Record<string, string> = {
  HIGH: 'bg-red-100 text-red-800',
  MEDIUM: 'bg-yellow-100 text-yellow-800',
  LOW: 'bg-green-100 text-green-800',
};

const STATUS_BANNER: Record<
  ObservationStatus,
  { wrap: string; icon: string; text: string; Icon: typeof CheckCircle }
> = {
  OPEN: { wrap: 'border-blue-200 bg-blue-50', icon: 'text-blue-600', text: 'text-blue-800', Icon: Circle },
  COMPLETED: {
    wrap: 'border-green-200 bg-green-50',
    icon: 'text-green-600',
    text: 'text-green-800',
    Icon: CheckCircle,
  },
  OVERDUE: { wrap: 'border-red-200 bg-red-50', icon: 'text-red-600', text: 'text-red-800', Icon: AlertCircle },
  CLOSED: { wrap: 'border-gray-200 bg-gray-50', icon: 'text-gray-600', text: 'text-gray-800', Icon: CheckCircle },
};

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDateTime = (iso: string | null | undefined) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const userFullName = (u: { first_name: string; last_name: string } | null | undefined) => {
  if (!u) return '—';
  return `${u.first_name} ${u.last_name}`.trim() || '—';
};

export const ObservationDetail: React.FC = () => {
  const { id, clientName } = useParams<{ id: string; clientName?: string }>();
  const { projectIdNumber: numericProjectId } = useCurrentProject();
  const navigate = useNavigate();
  const location = useLocation();
  const listPath = location.pathname.replace(/\/[^/]+$/, '');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDeleteObservationDialogOpen, setIsDeleteObservationDialogOpen] = useState(false);
  const [attachmentToDeleteId, setAttachmentToDeleteId] = useState<string | null>(null);
  const { data: item, isLoading, error } = useObservation(id, numericProjectId);
  const { updateItem, deleteItem, uploadAttachment, deleteAttachment } = useObservationMutations(numericProjectId);
  const { allProjects } = useAllUserProjects(clientName);
  const projectName = allProjects.find((p) => p.id === numericProjectId)?.name ?? '';

  // Auto-start the detail-view guided tour once the observation has
  // loaded. Skipped if a tour is already active (e.g. the combined
  // tour navigated us here from the list page) or if the user already
  // saw the combined tour, which covers the same content.
  const { hydrated: tourHydrated, active, startTour, hasSeen } = useTour();
  useEffect(() => {
    if (!tourHydrated) return;
    if (active) return;
    if (!item) return;
    if (hasSeen(OBSERVATIONS_COMBINED_TOUR_ID)) return;
    void startTour(OBSERVATIONS_DETAIL_TOUR_ID);
  }, [tourHydrated, active, startTour, hasSeen, item]);

  const handleStatusChange = (status: ObservationStatus) => {
    if (!id || !item?.can_edit) return;
    updateItem.mutate({ id, data: { status } });
  };

  const handleDelete = async () => {
    if (!id) return;
    await deleteItem.mutateAsync(id);
    navigate(listPath);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    await uploadAttachment.mutateAsync({ observationId: id, file });
    e.target.value = '';
  };

  const handleDeleteAttachment = (attachmentId: string) => {
    setAttachmentToDeleteId(attachmentId);
  };

  const confirmDeleteAttachment = () => {
    if (!id || !attachmentToDeleteId) return;
    deleteAttachment.mutate(
      { attachmentId: attachmentToDeleteId, observationId: id },
      { onSuccess: () => setAttachmentToDeleteId(null) },
    );
  };

  const handlePrint = () => {
    if (!item) return;
    const origin = window.location.origin;

    const fields: [string, string][] = [
      ['Status', STATUS_LABELS[item.status]],
      ['Priority', PRIORITY_LABELS[item.priority]],
      ['Due Date', formatDate(item.due_date)],
      ['Assignee', userFullName(item.assignee)],
      ['Type', item.observation_type?.name ?? '—'],
      ['Location', item.location?.name ?? '—'],
      ['Trade', item.trade?.name ?? '—'],
      ['Spec Section', item.spec_section || '—'],
      ['Notification Date', formatDate(item.notification_date)],
      ['Created By', userFullName(item.created_by)],
      ['Created At', formatDateTime(item.created_at)],
      ['Updated At', formatDateTime(item.updated_at)],
    ];

    const metaHtml = fields
      .map(
        ([label, value]) =>
          `<div><span class="label">${escapeHtml(label)}:</span> ${escapeHtml(value)}</div>`,
      )
      .join('');

    const safetyRows = [
      ['Hazard', item.hazard],
      ['Contributing Behavior', item.contributing_behavior],
      ['Contributing Condition', item.contributing_condition],
    ].filter(([, value]) => value);
    const safetyHtml = safetyRows.length
      ? `<div class="section"><h2>Safety / Quality</h2>${safetyRows
          .map(
            ([label, value]) =>
              `<p><strong>${escapeHtml(label as string)}:</strong> ${escapeHtml(value as string)}</p>`,
          )
          .join('')}</div>`
      : '';

    const distributionHtml = item.distribution_list.length
      ? `<div class="section"><h2>Distribution List</h2><p>${escapeHtml(
          item.distribution_list.map((u) => `${u.first_name} ${u.last_name}`.trim()).join(', '),
        )}</p></div>`
      : '';

    const attachmentsHtml = item.attachments.length
      ? `<div class="section"><h2>Attachments (${item.attachments.length})</h2><ul>${item.attachments
          .map((att) => `<li>${escapeHtml(att.file.split('/').pop() ?? att.file)}</li>`)
          .join('')}</ul></div>`
      : '';

    const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>OBS-${item.number} ${escapeHtml(item.title)}</title>
<style>
  @page { size: auto; margin: 0.6in; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #111827; margin: 0; }
  .header { position: relative; text-align: center; border-bottom: 2px solid #111827; padding-bottom: 12px; margin-bottom: 18px; }
  .header .logo { position: absolute; top: 0; right: 0; height: 40px; width: auto; }
  .header .project { font-size: 13px; font-weight: 600; color: #374151; margin: 0 0 2px; }
  .header h1 { font-size: 24px; font-weight: 700; margin: 0 0 4px; }
  .header .subtitle { font-size: 12px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: #6b7280; }
  .meta { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 24px; font-size: 12px; margin-bottom: 20px; }
  .meta .label { color: #6b7280; font-weight: 600; }
  .section { border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px 14px; margin-bottom: 12px; break-inside: avoid; page-break-inside: avoid; }
  .section h2 { font-size: 14px; font-weight: 700; margin: 0 0 8px; }
  .section p { font-size: 12px; margin: 0 0 6px; white-space: pre-wrap; }
  .section p:last-child { margin-bottom: 0; }
  .section ul { margin: 0; padding-left: 18px; }
  .section li { font-size: 12px; }
</style>
</head>
<body>
  <div class="header">
    <img class="logo" src="${origin}/static/images/iv360-p-300.webp" alt="Ironview 360" />
    ${projectName ? `<div class="project">${escapeHtml(projectName)}</div>` : ''}
    <h1>${escapeHtml(item.title)}</h1>
    <div class="subtitle">Observation OBS-${item.number} &middot; ${escapeHtml(STATUS_LABELS[item.status])}</div>
  </div>
  <div class="meta">${metaHtml}</div>
  <div class="section"><h2>Description</h2><p>${escapeHtml(item.description || 'No description provided.')}</p></div>
  ${safetyHtml}
  ${distributionHtml}
  ${attachmentsHtml}
  <script>
    window.onload = function () { window.focus(); window.print(); };
    window.onafterprint = function () { window.close(); };
  </script>
</body>
</html>`;

    const printWindow = window.open('', '_blank', 'width=900,height=1200');
    if (!printWindow) return;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  if (isLoading) {
    return (
      <BaseLayout>
        <div className="p-6 space-y-4 max-w-6xl mx-auto">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Skeleton className="h-16" /><Skeleton className="h-16" /><Skeleton className="h-16" /><Skeleton className="h-16" />
          </div>
          <Skeleton className="h-48 w-full" />
        </div>
      </BaseLayout>
    );
  }

  if (error || !item) {
    return (
      <BaseLayout>
        <div className="p-6 max-w-6xl mx-auto">
          <Button variant="ghost" onClick={() => navigate(listPath)}><ArrowLeft className="mr-2 h-4 w-4" />Back</Button>
          <p className="mt-4 text-destructive">Failed to load observation.</p>
        </div>
      </BaseLayout>
    );
  }

  const hasSafetyData = item.hazard || item.contributing_behavior || item.contributing_condition;
  const nonImageAttachments = item.attachments.filter(
    (att) => !isLikelyImageAttachment(att.file_url ?? att.file),
  );

  const banner = STATUS_BANNER[item.status];
  const BannerIcon = banner.Icon;
  const bannerMessage =
    item.status === 'COMPLETED'
      ? `Completed on ${formatDate(item.updated_at)}`
      : item.status === 'CLOSED'
        ? `Closed on ${formatDate(item.updated_at)}`
        : item.status === 'OVERDUE'
          ? `Overdue${item.due_date ? ` — was due ${formatDate(item.due_date)}` : ''}`
          : `Open${item.due_date ? ` — due ${formatDate(item.due_date)}` : ''}`;

  return (
    <BaseLayout>
      <div className="mx-auto w-full min-w-0 max-w-6xl space-y-4 break-words p-4 sm:space-y-6 md:p-6">
        {/* ── Header ────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <Button
              variant="outline"
              size="icon"
              className="flex-shrink-0"
              onClick={() => navigate(listPath)}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <div className="flex min-w-0 flex-col items-start gap-0.5 sm:flex-row sm:items-start sm:gap-2">
                {item.is_private && <Lock className="h-5 w-5 text-muted-foreground flex-shrink-0" />}
                <span className="shrink-0 text-sm font-mono text-muted-foreground">OBS-{item.number}</span>
                <h1 className="min-w-0 break-words text-2xl font-bold tracking-tight md:text-3xl lg:truncate">
                  {item.title}
                </h1>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <Badge variant="outline" className={PRIORITY_BADGE[item.priority]}>
                  {PRIORITY_LABELS[item.priority]} Priority
                </Badge>
                <span className="text-sm text-muted-foreground">
                  Created {formatDateTime(item.created_at)}
                  {item.created_by && ` by ${userFullName(item.created_by)}`}
                </span>
              </div>
            </div>
          </div>

          <div
            className="w-full flex-shrink-0 lg:w-auto"
            data-tour-id="observation-detail-header-actions"
          >
            <div className="hidden items-center gap-2 lg:flex">
              {item.hotspot_detail && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => {
                    const h = item.hotspot_detail!;
                    const params = new URLSearchParams({ photo: String(h.photo_id), hotspot: String(h.id), pitch: String(h.pitch), yaw: String(h.yaw) });
                    const base = location.pathname.replace(/\/observations\/.*$/, '');
                    navigate(`${base}/asbuilds?${params.toString()}`);
                  }}
                >
                  <Crosshair className="h-4 w-4" /> View Hotspot
                </Button>
              )}

              <Button variant="outline" size="sm" className="gap-1.5" onClick={handlePrint}>
                <Printer className="h-4 w-4" />
                Print PDF
              </Button>

              <Select value={item.status} onValueChange={handleStatusChange} disabled={!item.can_edit}>
                <SelectTrigger className="h-9 w-40 min-w-0 overflow-hidden">
                  <span className="min-w-0 flex-1 truncate text-left font-medium">
                    {STATUS_LABELS[item.status]}
                  </span>
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(STATUS_LABELS) as ObservationStatus[]).map((s) => (
                    <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button variant="destructive" size="sm" onClick={() => setIsDeleteObservationDialogOpen(true)} disabled={!item.can_delete || deleteItem.isPending}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 lg:hidden">
              <Select value={item.status} onValueChange={handleStatusChange} disabled={!item.can_edit}>
                <SelectTrigger className="h-10 min-w-0 w-full overflow-hidden">
                  <span className="min-w-0 flex-1 truncate text-left font-medium">
                    {STATUS_LABELS[item.status]}
                  </span>
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(STATUS_LABELS) as ObservationStatus[]).map((s) => (
                    <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="h-10 gap-2">
                    <MoreHorizontal className="h-4 w-4" />
                    Actions
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 max-w-[calc(100vw-2rem)]">
                  {item.hotspot_detail && (
                    <DropdownMenuItem
                      onSelect={() => {
                        const h = item.hotspot_detail!;
                        const params = new URLSearchParams({ photo: String(h.photo_id), hotspot: String(h.id), pitch: String(h.pitch), yaw: String(h.yaw) });
                        const base = location.pathname.replace(/\/observations\/.*$/, '');
                        navigate(`${base}/asbuilds?${params.toString()}`);
                      }}
                    >
                      <Crosshair className="mr-2 h-4 w-4" />
                      View hotspot
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onSelect={handlePrint}>
                    <Printer className="mr-2 h-4 w-4" />
                    Print PDF
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => setIsDeleteObservationDialogOpen(true)}
                    disabled={!item.can_delete || deleteItem.isPending}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete observation
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* ── Status banner ─────────────────────────────────────────── */}
        <Card className={banner.wrap}>
          <CardContent className="p-4 sm:p-6 sm:py-4">
            <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-start gap-2 sm:items-center">
                <BannerIcon className={`h-5 w-5 shrink-0 ${banner.icon}`} />
                <span className={`min-w-0 break-words text-sm font-medium ${banner.text}`}>{bannerMessage}</span>
              </div>
              <Badge className={`shrink-0 ${STATUS_BADGE[item.status]}`}>{STATUS_LABELS[item.status]}</Badge>
            </div>
          </CardContent>
        </Card>

        {/* ── Main content: 2/3 + 1/3 on desktop ───────────────────── */}
        <div className="grid min-w-0 grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-3">
          {/* Left column */}
          <div className="min-w-0 space-y-4 sm:space-y-6 lg:col-span-2">
            {/* Details */}
            <Card data-tour-id="observation-detail-metadata">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                  <div>
                    <dt className="font-medium text-muted-foreground">Priority</dt>
                    <dd className="mt-1">
                      <Badge variant="outline" className={PRIORITY_BADGE[item.priority]}>
                        {PRIORITY_LABELS[item.priority]}
                      </Badge>
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" /> Due Date
                    </dt>
                    <dd className="mt-0.5">{formatDate(item.due_date)}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Type</dt>
                    <dd className="mt-0.5">{item.observation_type?.name ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Location</dt>
                    <dd className="mt-0.5">{item.location?.name ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Trade</dt>
                    <dd className="mt-0.5">{item.trade?.name ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Spec Section</dt>
                    <dd className="mt-0.5">{item.spec_section || '—'}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" /> Notification Date
                    </dt>
                    <dd className="mt-0.5">{formatDate(item.notification_date)}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground flex items-center gap-1">
                      <User className="h-3.5 w-3.5" /> Created By
                    </dt>
                    <dd className="mt-0.5">{userFullName(item.created_by)}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" /> Created At
                    </dt>
                    <dd className="mt-0.5">{formatDateTime(item.created_at)}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" /> Updated At
                    </dt>
                    <dd className="mt-0.5">{formatDateTime(item.updated_at)}</dd>
                  </div>
                </dl>
              </CardContent>
            </Card>

            {/* Description */}
            <Card data-tour-id="observation-detail-description">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Description
                </CardTitle>
              </CardHeader>
              <CardContent>
                {item.description ? (
                  <p className="text-sm whitespace-pre-wrap">{item.description}</p>
                ) : (
                  <p className="text-sm text-muted-foreground italic">No description provided.</p>
                )}
              </CardContent>
            </Card>

            {/* Attachments */}
            <Card data-tour-id="observation-detail-attachments">
              <CardHeader className="gap-3 p-4 pb-2 sm:flex-row sm:items-center sm:justify-between sm:p-6 sm:pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Paperclip className="h-5 w-5" />
                  Attachments ({item.attachments.length})
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-full sm:w-auto"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadAttachment.isPending}
                >
                  <Upload className="mr-1.5 h-3.5 w-3.5" /> Attach
                </Button>
                <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} />
              </CardHeader>
              <CardContent>
                {item.attachments.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No attachments yet.</p>
                ) : (
                  <div className="space-y-3">
                    <AttachmentPhotoGallery
                      attachments={item.attachments}
                      onDelete={handleDeleteAttachment}
                      isDeleting={deleteAttachment.isPending}
                    />
                    {nonImageAttachments.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground">Other files</p>
                        <ul className="space-y-2">
                          {nonImageAttachments.map((att) => (
                            <li key={att.id} className="flex min-w-0 items-center justify-between gap-2">
                              <a href={att.file_url ?? att.file} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-sm text-primary hover:underline">{att.file.split('/').pop()}</a>
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:text-destructive flex-shrink-0" onClick={() => handleDeleteAttachment(att.id)} disabled={deleteAttachment.isPending}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right column */}
          <div className="min-w-0 space-y-4 sm:space-y-6 lg:col-span-1">
            {/* People */}
            <Card data-tour-id="observation-detail-people">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  People
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Assignee</p>
                    <p className="text-sm font-medium">{userFullName(item.assignee)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Created By</p>
                    <p className="text-sm font-medium">{userFullName(item.created_by)}</p>
                  </div>
                </div>
                {item.distribution_list.length > 0 && (
                  <>
                    <hr className="my-3 border-border" />
                    <div>
                      <p className="text-xs text-muted-foreground mb-1.5">Distribution List</p>
                      <div className="flex flex-wrap gap-1.5">
                        {item.distribution_list.map((u) => (
                          <Badge key={u.id} variant="secondary" className="text-xs font-normal">{u.first_name} {u.last_name}</Badge>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {hasSafetyData && (
              <Card data-tour-id="observation-detail-safety">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ShieldAlert className="h-5 w-5" />
                    Safety / Quality
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {item.hazard && (
                    <div>
                      <p className="text-xs text-muted-foreground">Hazard</p>
                      <p className="text-sm whitespace-pre-wrap">{item.hazard}</p>
                    </div>
                  )}
                  {item.contributing_behavior && (
                    <div>
                      <p className="text-xs text-muted-foreground">Contributing Behavior</p>
                      <p className="text-sm whitespace-pre-wrap">{item.contributing_behavior}</p>
                    </div>
                  )}
                  {item.contributing_condition && (
                    <div>
                      <p className="text-xs text-muted-foreground">Contributing Condition</p>
                      <p className="text-sm whitespace-pre-wrap">{item.contributing_condition}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      <Dialog open={isDeleteObservationDialogOpen} onOpenChange={setIsDeleteObservationDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete observation?</DialogTitle>
            <DialogDescription>
              This will permanently remove this observation and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteObservationDialogOpen(false)} disabled={deleteItem.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                await handleDelete();
                setIsDeleteObservationDialogOpen(false);
              }}
              disabled={deleteItem.isPending}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={attachmentToDeleteId !== null} onOpenChange={(open) => { if (!open) setAttachmentToDeleteId(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove attachment?</DialogTitle>
            <DialogDescription>
              This file will be removed from the observation.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttachmentToDeleteId(null)} disabled={deleteAttachment.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteAttachment} disabled={deleteAttachment.isPending}>
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </BaseLayout>
  );
};
